import HorizontalChipsWithTooltip from '@common/components/custom/CustomInputs/HorizontalChipsWithTooltip';
import CarContainer1, { NormalCard, CarListingCard } from '@common/pages/Dashboard/components/CarContainer1';
import CarsList from '@common/pages/Dashboard/components/CarsList';
import { carContainerTypes } from '@common/utils/constants/ui/carContainers';
import { years } from '@common/utils/constants/years';
import { CancelTwoTone, Check } from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import debounce from 'lodash.debounce';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { baseImageUrl } from '../../../../api';
import { reducerPaths } from '../../../../app/reducerPaths';
import theme from '../../../../theme';
import {
  useLazyGetLiveAuctionVehiclesQuery,
  useLazyGetOwnLiveAuctionVehiclesQuery,
} from '../../../../toolkit/liveAuction/liveAuctionApi';
import {
  useGetVehicleModelsMutation,
  useLazyGetVehicleMakesQuery,
} from '../../../../toolkit/upcomingCars/upcomingCarsApi';
import { exportAuctionTimeInMins, localAuction, localAuctionTimeInMins } from '../../../../utils/constants/auction';
import { convertFromUTCToGivenTimezone, convertUtcStringFormat } from '../../../../utils/methods/dateTime';
import MyCarsOrOtherCarsButton, { defaultValue } from '../common/MyCarsOrOtherCarsButton';
import { useListenForNewLiveAuctionCars } from './customHooks/useListenForNewLiveAuctionCars';

const LiveAuctionCarsList = ({ onCarClick, selectedCar, setSelectedCar, onMyCarsOrOtherCars }) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.between('sm', 'md'));

  // Translation hook
  const { t } = useTranslation('liveAuction');

  // Helper function to get selected makes render value - Fixed with translations
  const getSelectedMakesRenderValue = (selected) => {
    if (!selected || selected.filter((item) => item && item.value).length === 0) {
      return t('select');
    }
    const filteredSelected = selected.filter((item) => item && item.value);
    if (filteredSelected.length === 1) {
      return filteredSelected[0]?.label || t('select');
    }
    return `${filteredSelected.length} ${t('selectMake')}`;
  };

  // Helper function to get selected models render value - Fixed with translations
  const getSelectedModelsRenderValue = (selected) => {
    if (!selected || selected.filter((item) => item && item.value).length === 0) {
      return t('select');
    }
    const filteredSelected = selected.filter((item) => item && item.value);
    if (filteredSelected.length === 1) {
      return filteredSelected[0]?.label || t('select');
    }
    return `${filteredSelected.length} ${t('selectModel')}`;
  };

  // static data start...
  const paginationDataInit = React.useMemo(() => {
    return {
      current_page: 1,
      per_page: 10,
      isFetching: true,
      data: [],
    };
  }, []);
  // static data end...

  // useSelector start...
  const auth = useSelector((store) => store[reducerPaths.auth]);
  // useSelector end...

  // useState variables start...
  const [allMakes, setAllMakes] = React.useState([]);
  const [allModels, setAllModels] = React.useState([]);

  // Fixed: Changed from [""] to [] for proper empty state handling
  const [selectedMakes, setSelectedMakes] = React.useState([]);
  const [selectedModels, setSelectedModels] = React.useState([]);
  const [selectedYearFrom, setSelectedYearFrom] = React.useState('');
  const [selectedYearTo, setSelectedYearTo] = React.useState('');
  const [odometerFrom, setOdometerFrom] = React.useState('');
  const [odometerTo, setOdometerTo] = React.useState('');
  const [value, setValue] = React.useState(defaultValue);
  const [paginationData, setPaginationData] = React.useState(paginationDataInit);
  // useState variables end....

  const onNewCarInAuctionHandler = async (vehicle) => {
    await fetchPaginationDataHandler({
      params: {
        Year: selectedYearFrom,
        Make: selectedMakes
          ?.filter((item) => item && item.value)
          ?.map((item) => item?.label)
          ?.join(','), // Pass make names
        Model: selectedModels
          ?.filter((item) => item && item.value)
          ?.map((item) => item?.label)
          ?.join(','), // Pass model names
        from_year: selectedYearFrom,
        to_year: selectedYearTo,
        from_Mileage: odometerFrom,
        to_Mileage: odometerTo,
      },
    });
  };

  // custom hook calls start...
  const [getLiveAuctionVehicleMakesApiHandler] = useLazyGetVehicleMakesQuery();
  const [getVehicleModelsApiHandler] = useGetVehicleModelsMutation();
  const [getOwnLiveAuctionVehiclesApiHandler] = useLazyGetOwnLiveAuctionVehiclesQuery();
  const [getOthersLiveAuctionVehiclesApiHandler] = useLazyGetLiveAuctionVehiclesQuery();
  useListenForNewLiveAuctionCars({
    onNewCarInAuctionHandler
  });
  // custom hook calls end...

  // Handlers start...
  const handlePageChange = (e, page) => {
    setPaginationData((paginationData) => {
      return {
        ...paginationData,
        current_page: page,
      };
    });
  };

  const fetchPaginationDataHandler = async ({ params }) => {
    let res;
    setPaginationData((paginationData) => {
      return {
        ...paginationData,
        isFetching: true,
      };
    });

    if (value === 0) {
      res = await getOwnLiveAuctionVehiclesApiHandler({
        page: paginationData?.current_page,
        per_page: paginationData?.per_page,
        params,
      });
    } else if (value === 1) {
      res = await getOthersLiveAuctionVehiclesApiHandler({
        page: paginationData?.current_page,
        per_page: paginationData?.per_page,
        params,
      });
    }

    setPaginationData((paginationData) => {
      return {
        ...paginationData,
        data: res?.data?.Vehicle?.data || res?.data?.Vehicle || [],
        isFetching: false,
      };
    });
  };

  const getLiveAuctionRemainingSeconds = (car) => {
    let liveAuctionDateTime, liveAuctionEndDate, liveAuctionRemainingSeconds;

    liveAuctionDateTime = convertFromUTCToGivenTimezone({
      dateStr: convertUtcStringFormat(car?.Live_Auction_DateTime),
      timezone: auth?.timezone,
    });

    liveAuctionEndDate = new Date(liveAuctionDateTime);
    liveAuctionEndDate.setMinutes(
      liveAuctionEndDate.getMinutes() +
        (car?.auction_type === localAuction ? localAuctionTimeInMins : exportAuctionTimeInMins)
    );

    liveAuctionRemainingSeconds = Math.floor(
      liveAuctionEndDate.getTime() - new Date().getTime() > 1000
        ? (liveAuctionEndDate.getTime() - new Date().getTime()) / 1000
        : 0
    );

    return liveAuctionRemainingSeconds;
  };
  // Handlers end...

  // useEffect start...
  React.useEffect(() => {
    if (
      selectedYearFrom ||
      (selectedMakes && selectedMakes.length > 0) ||
      (selectedModels && selectedModels.length > 0) ||
      selectedYearTo ||
      odometerFrom ||
      odometerTo
    ) {
      const debouncedFetchData = debounce(async () => {
        await fetchPaginationDataHandler({
          params: {
            Year: selectedYearFrom,
            Make: selectedMakes
              ?.filter((item) => item && item.value)
              ?.map((item) => item?.label)
              ?.join(','), // Pass make names
            Model: selectedModels
              ?.filter((item) => item && item.value)
              ?.map((item) => item?.label)
              ?.join(','), // Pass model names
            from_year: selectedYearFrom,
            to_year: selectedYearTo,
            from_Mileage: odometerFrom,
            to_Mileage: odometerTo,
          },
        });

        // dispatch(
        //   setLiveAuctionMultipleFields({
        //     liveAuctionData: {
        //       ...liveAuction?.liveAuctionData,
        //       data: res?.data?.Vehicle?.data || []
        //     }
        //   })
        // );
      }, 1000);

      debouncedFetchData();

      return () => {
        debouncedFetchData.cancel(); // Cleanup function to cancel pending calls
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYearFrom, selectedMakes, selectedModels, selectedYearTo, odometerFrom, odometerTo]);

  React.useEffect(() => {
    (async () => {
      await fetchPaginationDataHandler({});
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, paginationData?.current_page]);

  React.useEffect(() => {
    (async () => {
      const res = await getLiveAuctionVehicleMakesApiHandler({
        endpoint: `/get-year-make`,
      });

      setAllMakes(
        res?.data?.Make?.map?.((item) => {
          return {
            value: item?.make_id,
            label: item?.make_name,
          };
        })
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    (async () => {
      if (selectedMakes && selectedMakes.length > 0) {
        const res = await getVehicleModelsApiHandler({
          endpoint: `/get-model`,
          payload: {
            makes: selectedMakes
              ?.filter((item) => item && item.value)
              ?.map?.((item) => item?.value)
              ?.join(','),
          },
        });

        const allModels = [];

        if (res?.data?.Model) {
          for (let i = 0; i < Object.entries(res?.data?.Model)?.length; i++) {
            allModels.push(...(Object.entries(res?.data?.Model)?.[i]?.[1] || []));
          }

          setAllModels(
            allModels?.map?.((item) => {
              return {
                value: item?.model_id,
                label: item?.model_name,
              };
            }) || []
          );
        }
      } else {
        setAllModels([]);
        setSelectedModels([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMakes]);

  React.useEffect(() => {
    if (onMyCarsOrOtherCars) {
      onMyCarsOrOtherCars(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  // useEffect start...

  console.log(selectedCar, `selectedCar`);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        display={'flex'}
        width={'100%'}
        sx={{
          borderRadius: '24px',
          height: {
            lg: `calc(100vh - 94px)`,
            md: `calc(100vh - 94px)`,
            xs: paginationData?.data?.length === 0 ? 'calc(100vh - 118px)' : 'auto',
          },
          position: 'relative',
        }}
      >
        <Box
          sx={{
            flex: '1',
            display: 'flex',
            flexDirection: 'column',
            background: theme.palette.background.paper,
            borderRadius: '24px',
            width: '100%',
          }}
        >
          <Paper elevation={0} sx={{ borderRadius: '24px' }}>
            <Box sx={{ padding: { xs: 2, sm: 3, md: 3 } }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 2, sm: 1 },
                }}
              >
                <Typography
                  variant={'h1'}
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {t('Live Auction')}
                </Typography>

                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', width: { xs: '100%', sm: 'auto' } }}
                  gap={2}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      border: '1px solid',
                      borderColor: 'border.main',
                      p: '4px',
                      borderRadius: '0.5rem',
                      overflow: 'hidden',
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    <MyCarsOrOtherCarsButton value={value} setValue={setValue} fullWidth={true} />
                  </Box>
                </Box>
              </Box>

              {/* Filters Section */}
              <Grid container spacing={{ xs: 2, sm: 2, md: 2 }} sx={{ mt: 2 }}>
                {/* Select Make */}
                <Grid size={{ xs: 12, sm: 6, md: 6, xl: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <InputLabel>{t('selectMake')}</InputLabel>
                    <Select
                      value={selectedMakes}
                      onChange={(e) => {
                        const value = typeof e.target.value === 'string' ? [] : e.target.value;
                        setSelectedMakes(value.filter((item) => item && item.value));
                      }}
                      displayEmpty
                      fullWidth
                      multiple={true}
                      renderValue={(selected) => getSelectedMakesRenderValue(selected)}
                      size={isMobile ? 'small' : 'medium'}
                      endAdornment={
                        <Box
                          sx={{
                            paddingRight: '1rem',
                          }}
                        >
                          {selectedMakes?.filter((item) => item && item.value)?.length > 0 ? (
                            <Button
                              sx={{
                                padding: '0',
                                minWidth: '0',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedMakes([]);
                              }}
                            >
                              <CancelTwoTone
                                sx={{
                                  color: theme.palette.text.primary,
                                }}
                              />
                            </Button>
                          ) : null}
                        </Box>
                      }
                    >
                      {allMakes?.map?.((make) => {
                        return (
                          <MenuItem key={make?.value} value={make}>
                            <ListItemText primary={make?.label} />
                            {selectedMakes?.some((selected) => selected?.value === make?.value) ? <Check /> : ''}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </Box>
                </Grid>

                {/* Select Model */}
                <Grid size={{ xs: 12, sm: 6, md: 6, xl: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <InputLabel>{t('selectModel')}</InputLabel>
                    <Select
                      value={selectedModels}
                      onChange={(e) => {
                        const value = typeof e.target.value === 'string' ? [] : e.target.value;
                        setSelectedModels(value.filter((item) => item && item.value));
                      }}
                      displayEmpty
                      fullWidth
                      multiple={true}
                      disabled={selectedMakes.filter((item) => item && item.value).length === 0}
                      renderValue={(selected) => getSelectedModelsRenderValue(selected)}
                      size={isMobile ? 'small' : 'medium'}
                      endAdornment={
                        <Box
                          sx={{
                            paddingRight: '1rem',
                          }}
                        >
                          {selectedModels?.filter((item) => item && item.value)?.length > 0 ? (
                            <Button
                              sx={{
                                padding: '0',
                                minWidth: '0',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedModels([]);
                              }}
                            >
                              <CancelTwoTone
                                sx={{
                                  color: theme.palette.text.primary,
                                }}
                              />
                            </Button>
                          ) : null}
                        </Box>
                      }
                    >
                      {allModels?.map((item) => {
                        return (
                          <MenuItem key={item?.value} value={item}>
                            <ListItemText primary={item?.label} />
                            {selectedModels?.some((selected) => selected?.value === item?.value) ? <Check /> : ''}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </Box>
                </Grid>

                {/* Select Year */}
                <Grid size={{ xs: 12, sm: 6, md: 6, xl: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <InputLabel>{t('selectYear')}</InputLabel>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                      }}
                    >
                      <Select
                        value={selectedYearFrom}
                        onChange={(e) => setSelectedYearFrom(e.target.value)}
                        displayEmpty
                        fullWidth
                        size={isMobile ? 'small' : 'medium'}
                        endAdornment={
                          <Box
                            sx={{
                              paddingRight: '1rem',
                            }}
                          >
                            {selectedYearFrom ? (
                              <Button
                                sx={{
                                  padding: '0',
                                  minWidth: '0',
                                }}
                                onClick={(e) => {
                                  setSelectedYearFrom('');
                                }}
                              >
                                <CancelTwoTone
                                  sx={{
                                    color: theme.palette.text.primary,
                                  }}
                                />
                              </Button>
                            ) : null}
                          </Box>
                        }
                      >
                        <MenuItem value="">{t('from')}</MenuItem>
                        {years?.map((year) => {
                          return (
                            <MenuItem key={year} value={year}>
                              {year}
                            </MenuItem>
                          );
                        })}
                      </Select>

                      <Select
                        value={selectedYearTo}
                        onChange={(e) => setSelectedYearTo(e.target.value)}
                        displayEmpty
                        fullWidth
                        size={isMobile ? 'small' : 'medium'}
                        endAdornment={
                          <Box
                            sx={{
                              paddingRight: '1rem',
                            }}
                          >
                            {selectedYearTo ? (
                              <Button
                                sx={{
                                  padding: '0',
                                  minWidth: '0',
                                }}
                                onClick={(e) => {
                                  setSelectedYearTo('');
                                }}
                              >
                                <CancelTwoTone
                                  sx={{
                                    color: theme.palette.text.primary,
                                  }}
                                />
                              </Button>
                            ) : null}
                          </Box>
                        }
                      >
                        <MenuItem value="">{t('to')}</MenuItem>

                        {years?.map((year) => {
                          return (
                            <MenuItem key={year} value={year}>
                              {year}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </Box>
                  </Box>
                </Grid>

                {/* Odometer */}
                <Grid size={{ xs: 12, sm: 6, md: 6, xl: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <InputLabel>{t('odometer')}</InputLabel>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                      }}
                    >
                      <TextField
                        type="number"
                        value={odometerFrom}
                        onChange={(e) => setOdometerFrom(e.target.value)}
                        placeholder={t('fromKm')}
                        fullWidth
                        size={isMobile ? 'small' : 'medium'}
                      />
                      <TextField
                        type="number"
                        value={odometerTo}
                        onChange={(e) => setOdometerTo(e.target.value)}
                        placeholder={t('toKm')}
                        fullWidth
                        size={isMobile ? 'small' : 'medium'}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* Selected Brands */}
              {selectedMakes?.filter((item) => item && item.value)?.length > 0 ? (
                <Box
                  sx={{
                    marginTop: '0.25rem',
                  }}
                >
                  <HorizontalChipsWithTooltip arr={selectedMakes} setSelectedArr={setSelectedMakes} />
                </Box>
              ) : null}
            </Box>
          </Paper>

          <Box
            sx={{
              height: 'calc(100% - 96px)',
              overflowY: 'auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              scrollbarWidth: 'none !important' /* Firefox */,
              '-ms-overflow-style': 'none' /* IE and Edge */,
              padding: { xs: 2, sm: 3, md: 3 },
              marginBottom: { xs: 3, sm: 3, md: 3 },
            }}
          >
            {paginationData?.isFetching ? (
              // Skeleton UI
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                }}
              >
                {/* Grid of skeleton car cards */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      lg: 'repeat(auto-fill, minmax(350px, 1fr))',
                      xs: 'repeat(auto-fill, minmax(300px, 1fr))',
                    },
                    gap: 2,
                    width: '100%',
                  }}
                >
                  {[...Array(8)].map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 2,
                        padding: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    >
                      {/* Car image skeleton */}
                      <Box sx={{ position: 'relative', marginBottom: 2 }}>
                        <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 1.5 }} />

                        {/* Timer skeleton in top right */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            right: 8,
                          }}
                        >
                          <Skeleton variant="rectangular" width={130} height={40} sx={{ borderRadius: 1 }} />
                        </Box>
                      </Box>

                      {/* Car details skeleton */}
                      <Box sx={{ marginBottom: 2 }}>
                        {/* Car title */}
                        <Skeleton variant="text" width="80%" height={24} sx={{ marginBottom: 1 }} />

                        {/* VIN */}
                        <Skeleton variant="text" width="60%" height={16} sx={{ marginBottom: 1 }} />

                        {/* Car specs grid */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, marginBottom: 2 }}>
                          {[...Array(4)].map((_, specIndex) => (
                            <Box key={specIndex}>
                              <Skeleton variant="text" width="50%" height={14} sx={{ marginBottom: 0.5 }} />
                              <Skeleton variant="text" width="70%" height={16} />
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : paginationData?.data?.length === 0 ? (
              <Typography
                sx={{
                  fontWeight: '500',
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                  textAlign: 'center',
                  marginTop: { xs: '-100px', md: '-200px' },
                }}
              >
                {t('No cars are listed at the moment. Please check back later as new vehicles arrive regularly.')}
              </Typography>
            ) : (
              <Box sx={{ height: '100%', width: '100%', padding: { xs: 0, sm: 0 } }}>
                <CarsList
                  showRemainingTime={true}
                  // cars={cars}
                  cars={paginationData?.data?.map?.((vehicle) => {
                    return {
                      ...vehicle,
                      vin: vehicle?.VIN,
                      year: vehicle?.Year,
                      make: vehicle?.Make,
                      model: vehicle?.Model,
                      odometer: vehicle?.Mileage,
                      image: vehicle?.Exterior_Image
                        ? `${baseImageUrl}/${vehicle?.Vehicle_Id}/${vehicle?.Exterior_Image}`
                        : ``,
                    };
                  })}
                  Component={CarContainer1}
                  ChildComponent={CarListingCard}
                  onClick={(car) => {
                    console.log(car, `CarsList`);
                    if (onCarClick) {
                      onCarClick(car);
                    }
                    // setSelectedCar(car);
                  }}
                  timerRemainingSecondsHandler={getLiveAuctionRemainingSeconds}
                  cardType={carContainerTypes.LIVE_AUCTION}
                />
              </Box>
            )}
          </Box>
        </Box>
        {/* Pagination */}
        {paginationData?.last_page > 1 ? (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              position: 'absolute',
              bottom: '0px',
              padding: { xs: 1, sm: 0 },
            }}
          >
            <Pagination
              count={paginationData?.last_page}
              page={paginationData?.current_page}
              onChange={handlePageChange}
              size={isMobile ? 'small' : 'medium'}
              siblingCount={isMobile ? 0 : isTablet ? 1 : 1}
              boundaryCount={isMobile ? 1 : 2}
            />
          </Box>
        ) : null}
      </Box>
    </LocalizationProvider>
  );
};

export default LiveAuctionCarsList;
